import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import FrameImage from "../assets/images/Frame.png";
import { ImageCache } from "../utils/imageCache";

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholderClassName?: string;
  fallbackSrc?: string;
  onLoad?: () => void;
  onError?: () => void;
  loading?: "lazy" | "eager";
  useIntersectionObserver?: boolean;
  skeletonAnimation?: boolean;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = "",
  placeholderClassName = "",
  fallbackSrc,
  onLoad,
  onError,
  loading = "lazy",
  useIntersectionObserver = true,
  skeletonAnimation = true,
  width,
  height,
  style,
  onClick,
}) => {
  // Initialize state based on persistent cache
  const isCached = src && ImageCache.has(src);
  const [imageState, setImageState] = useState<"loading" | "loaded" | "error">(() => {
    return isCached ? "loaded" : "loading";
  });
  const [imageSrc, setImageSrc] = useState<string | null>(() => {
    return isCached ? src : null;
  });
  // If image is cached, skip IntersectionObserver entirely
  const [isVisible, setIsVisible] = useState(!useIntersectionObserver || isCached);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasLoadedRef = useRef(isCached);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Use provided fallback or default to Frame.png
  const defaultFallback = fallbackSrc || FrameImage;

  // Intersection Observer for lazy loading
  useEffect(() => {
    // Skip observer if not needed or image already cached
    if (!useIntersectionObserver || !containerRef.current || ImageCache.has(src)) {
      setIsVisible(true);
      return;
    }

    // If already loaded in this instance, skip observer
    if (hasLoadedRef.current) {
      setIsVisible(true);
      return;
    }

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Disconnect after triggering visibility
          observerRef.current?.disconnect();
        }
      },
      {
        rootMargin: "100px", // Start loading 100px before entering viewport
        threshold: 0.01,
      }
    );

    observerRef.current.observe(containerRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [useIntersectionObserver, src]);

  // Load image when visible
  useEffect(() => {
    if (!isVisible || !src) return;

    // If image is already cached, set loaded immediately
    if (ImageCache.has(src)) {
      if (!hasLoadedRef.current) {
        setImageSrc(src);
        setImageState("loaded");
        hasLoadedRef.current = true;
      }
      return;
    }

    // If already loaded in this instance, don't reload
    if (hasLoadedRef.current) {
      return;
    }

    const img = new Image();
    let isMounted = true;
    let handled = false;

    const handleLoad = () => {
      if (isMounted && !handled) {
        handled = true;
        ImageCache.set(src);
        setImageSrc(src);
        setImageState("loaded");
        hasLoadedRef.current = true;
        onLoad?.();
      }
    };

    const handleError = () => {
      if (isMounted && !handled) {
        handled = true;
        if (defaultFallback && defaultFallback !== src) {
          ImageCache.set(defaultFallback);
          setImageSrc(defaultFallback);
          setImageState("loaded");
          hasLoadedRef.current = true;
        } else {
          setImageState("error");
        }
        onError?.();
      }
    };

    img.onload = handleLoad;
    img.onerror = handleError;

    // Set src to trigger load (browser will use cache if available)
    img.src = src;

    // Check if image is already complete (browser cache)
    // This prevents showing skeleton for cached images
    if (img.complete && img.naturalHeight > 0) {
      // Image is in browser cache, set loaded immediately
      ImageCache.set(src);
      setImageSrc(src);
      setImageState("loaded");
      hasLoadedRef.current = true;
      handled = true;
      onLoad?.();
    }

    return () => {
      isMounted = false;
      handled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [isVisible, src]);

  const renderSkeleton = () => (
    <div
      className={classNames(
        "bg-gray-200 rounded-md flex items-center justify-center",
        skeletonAnimation ? "skeleton-pulse" : "animate-pulse",
        placeholderClassName,
        className
      )}
      style={{
        width: width || "100%",
        height: height || "200px",
        ...style,
      }}
    >
      <svg
        className="w-10 h-10 text-gray-400"
        fill="currentColor"
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  );

  const renderError = () => (
    <div
      className={classNames(
        "bg-gray-100 rounded-md flex flex-col items-center justify-center border-2 border-dashed border-gray-300",
        className
      )}
      style={{
        width: width || "100%",
        height: height || "200px",
        ...style,
      }}
    >
      <svg
        className="w-12 h-12 text-gray-400 mb-2"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.96-.833-2.73 0L4.084 16.5c-.77.833.192 2.5 1.732 2.5z"
        />
      </svg>
      <span className="text-gray-500 text-sm">Failed to load image</span>
    </div>
  );

  return (
    <div ref={containerRef} className="relative" onClick={onClick}>
      {imageState === "loading" && renderSkeleton()}
      {imageState === "error" && renderError()}
      {imageState === "loaded" && imageSrc && (
        <img
          ref={imgRef}
          src={imageSrc}
          alt={alt}
          className={classNames(
            "image-fade-in transition-opacity duration-300",
            imageState === "loaded" ? "opacity-100" : "opacity-0",
            className
          )}
          loading={loading}
          style={{
            width,
            height,
            ...style,
          }}
          onLoad={() => {
            if (imgRef.current) {
              imgRef.current.classList.add("opacity-100");
            }
          }}
        />
      )}
    </div>
  );
};

export default OptimizedImage;